import { useMemo, useState } from 'react';
import AdminShell from '@/components/maple-market/admin/AdminShell';
import DashboardPanel from '@/components/maple-market/admin/DashboardPanel';
import AppsPanel from '@/components/maple-market/admin/AppsPanel';
import AppDetailPanel, { type AppDetailTab } from '@/components/maple-market/admin/AppDetailPanel';
import AnalyticsPanel from '@/components/maple-market/admin/AnalyticsPanel';
import RevenuePanel from '@/components/maple-market/admin/RevenuePanel';
import EventsPanel from '@/components/maple-market/admin/EventsPanel';
import KeysPanel from '@/components/maple-market/admin/KeysPanel';
import DocsPanel from '@/components/maple-market/admin/DocsPanel';
import { type AdminPanel } from '@/components/maple-market/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useMarketplaceProducts } from '@/hooks/supabase/useMarketplaceProducts';
import { useMarketplaceMemberships } from '@/hooks/supabase/useMarketplaceMemberships';

interface MapleMarketAdminWindowProps {
  onExit: () => void;
}

function PlaceholderPanel({ title, description }: { title: string; description: string }) {
  return (
    <div className="p-6 lg:p-8">
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>{description}</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function MapleMarketAdminWindow({ onExit }: MapleMarketAdminWindowProps) {
  const { liveProducts, loading: productsLoading, reload, filterLiveProductsByIds } = useMarketplaceProducts();
  const {
    memberships,
    loading: membershipsLoading,
    isSiteAdmin,
    isVendor,
    accessibleProductIds,
    getRoleForProduct,
    canManageProduct,
    canManageTeam,
    listMembers,
    addMemberByEmail,
    updateMemberRole,
    removeMember,
  } = useMarketplaceMemberships();

  const [selectedPanel, setSelectedPanel] = useState<AdminPanel>('overview');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<AppDetailTab>('basic');
  const [returnPanel, setReturnPanel] = useState<AdminPanel>('apps');

  const accessibleProducts = useMemo(
    () => filterLiveProductsByIds(accessibleProductIds, isSiteAdmin),
    [accessibleProductIds, filterLiveProductsByIds, isSiteAdmin]
  );

  const selectedProduct = useMemo(
    () => accessibleProducts.find((product) => product.id === selectedProductId) ?? null,
    [accessibleProducts, selectedProductId]
  );

  const openProduct = (productId: string, initialTab: AppDetailTab = 'basic', sourcePanel: AdminPanel = selectedPanel) => {
    setReturnPanel(sourcePanel === 'app-detail' ? 'apps' : sourcePanel);
    setSelectedProductId(productId);
    setDetailTab(initialTab);
    setSelectedPanel('app-detail');
  };

  const handlePanelSelect = (panel: AdminPanel) => {
    if (panel !== 'app-detail') {
      setSelectedPanel(panel);
    }
  };

  const handleSaved = async () => {
    await reload();
  };

  if (!isSiteAdmin && !isVendor && !membershipsLoading) {
    return (
      <div className="p-6 lg:p-8">
        <Card>
          <CardHeader>
            <CardTitle>Admin access unavailable</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>You need a marketplace membership or site admin role to access this embedded dashboard.</p>
            <Button type="button" onClick={onExit}>Back to Store</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminShell selectedPanel={selectedPanel} onSelectPanel={handlePanelSelect} onExit={onExit}>
      {selectedPanel === 'overview' && (
        <DashboardPanel
          products={accessibleProducts}
          memberships={memberships}
          isSiteAdmin={isSiteAdmin}
          onOpenProduct={(productId) => openProduct(productId, 'basic', 'overview')}
          onSelectPanel={handlePanelSelect}
        />
      )}

      {selectedPanel === 'analytics' && (
        <AnalyticsPanel products={accessibleProducts} isSiteAdmin={isSiteAdmin} />
      )}

      {selectedPanel === 'apps' && (
        <AppsPanel
          products={accessibleProducts}
          loading={productsLoading || membershipsLoading}
          isSiteAdmin={isSiteAdmin}
          getRoleForProduct={getRoleForProduct}
          canManageProduct={canManageProduct}
          onOpenProduct={(productId, initialTab) => openProduct(productId, initialTab ?? 'basic', 'apps')}
        />
      )}

      {selectedPanel === 'revenue' && <RevenuePanel products={accessibleProducts} />}

      {selectedPanel === 'events' && (
        <EventsPanel products={accessibleProducts} onOpenProduct={(productId, initialTab) => openProduct(productId, initialTab ?? 'publish', 'events')} />
      )}

      {selectedPanel === 'team' && (
        <AppsPanel
          title="Users / Permissions"
          description="Choose a product to manage per-product owners, admins, developers, and moderators."
          products={accessibleProducts}
          loading={productsLoading || membershipsLoading}
          mode="team"
          isSiteAdmin={isSiteAdmin}
          getRoleForProduct={getRoleForProduct}
          canManageProduct={canManageProduct}
          onOpenProduct={(productId, initialTab) => openProduct(productId, initialTab ?? 'team', 'team')}
        />
      )}

      {selectedPanel === 'keys' && (
        <KeysPanel products={accessibleProducts} onOpenProduct={(productId) => openProduct(productId, 'keys', 'keys')} />
      )}
      {selectedPanel === 'docs' && <DocsPanel />}

      {selectedPanel === 'app-detail' && selectedProduct && (
        <AppDetailPanel
          product={selectedProduct}
          initialTab={detailTab}
          roleLabel={getRoleForProduct(selectedProduct.id) || (isSiteAdmin ? 'site admin' : 'read only')}
          canManageProduct={canManageProduct(selectedProduct.id)}
          canManageTeam={canManageTeam(selectedProduct.id)}
          isSiteAdmin={isSiteAdmin}
          onBack={() => setSelectedPanel(returnPanel)}
          onSaved={handleSaved}
          listMembers={listMembers}
          addMemberByEmail={addMemberByEmail}
          updateMemberRole={updateMemberRole}
          removeMember={removeMember}
        />
      )}

      {selectedPanel === 'app-detail' && !selectedProduct && (
        <div className="p-6 lg:p-8">
          <Card>
            <CardHeader>
              <CardTitle>Product unavailable</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>The selected product is no longer available in your admin scope.</p>
              <Button type="button" onClick={() => setSelectedPanel('apps')}>Back to applications</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {selectedPanel === 'settings' && (
        <PlaceholderPanel
          title="Settings"
          description="Vendor-specific preferences and organization details can be added here later without exposing a public admin route."
        />
      )}
    </AdminShell>
  );
}
